import { API_URLS } from "@/constants";
import apiRequest from "@/lib/axios";
import {
  BaseCountry,
  BaseCurrency,
  BaseLanguage,
  BaseRefFilter,
  GetList,
  Option,
  OrderMove,
  OrderMoveResult,
  PagingResponse,
} from "@/types";

export interface RefUrls {
  base: string;
  list: string;
  options: string;
  byId: (id: string) => string;
  // Only lists with an admin order have one.
  move?: (id: string) => string;
}

export interface RefService<T, F = BaseRefFilter> {
  getOptions: () => Promise<Option[]>;
  get: (id: string) => Promise<T>;
  getList: (data: GetList<F>) => Promise<PagingResponse<T>>;
  create: (data: Partial<T>) => Promise<T>;
  update: (id: string, data: Partial<T>) => Promise<T>;
  remove: (id: string) => Promise<T>;
  move: (id: string, data: OrderMove) => Promise<OrderMoveResult>;
}

export const makeRefService = <T, F = BaseRefFilter>(
  urls: RefUrls,
  label: string,
): RefService<T, F> => {
  // Per-instance options cache. Options endpoints rarely change and are
  // commonly fetched from multiple consumers in parallel (plus StrictMode's
  // double effect-invoke in dev), so dedupe to one in-flight request.
  let optionsPromise: Promise<Option[]> | null = null;
  const invalidate = () => {
    optionsPromise = null;
  };

  return {
    getOptions: () => {
      if (optionsPromise) return optionsPromise;
      optionsPromise = apiRequest<Option[]>({
        method: "GET",
        url: urls.options,
        errorMessage: `get ${label} options failed`,
      }).catch((error) => {
        // Don't poison the cache on failure - clear so the next caller retries.
        optionsPromise = null;
        throw error;
      });

      return optionsPromise;
    },
    get: (id) =>
      apiRequest({ method: "GET", url: urls.byId(id), errorMessage: `get ${label} failed` }),
    getList: (data) =>
      apiRequest({
        method: "POST",
        url: urls.list,
        data,
        errorMessage: `get ${label} list failed`,
      }),
    create: async (data) => {
      const result = await apiRequest<T>({
        method: "POST",
        url: urls.base,
        data,
        errorMessage: `create ${label} failed`,
      });
      invalidate();

      return result;
    },
    update: async (id, data) => {
      const result = await apiRequest<T>({
        method: "PUT",
        url: urls.byId(id),
        data,
        errorMessage: `update ${label} failed`,
      });
      invalidate();

      return result;
    },
    remove: async (id) => {
      const result = await apiRequest<T>({
        method: "DELETE",
        url: urls.byId(id),
        errorMessage: `delete ${label} failed`,
      });
      invalidate();

      return result;
    },
    move: async (id, data) => {
      if (!urls.move) throw new Error(`${label} has no order`);
      const result = await apiRequest<OrderMoveResult>({
        method: "PUT",
        url: urls.move(id),
        data,
        errorMessage: `move ${label} failed`,
      });
      invalidate();

      return result;
    },
  };
};

export const baseLanguageService = makeRefService<BaseLanguage>(
  {
    base: API_URLS.BASE_LANGUAGE.LANGUAGE,
    list: API_URLS.BASE_LANGUAGE.GET_LANGUAGES_LIST,
    options: API_URLS.BASE_LANGUAGE.GET_LANGUAGE_OPTIONS,
    byId: API_URLS.BASE_LANGUAGE.LANGUAGE_BY_ID,
    move: API_URLS.BASE_LANGUAGE.MOVE,
  },
  "language",
);

export const baseCountryService = makeRefService<BaseCountry>(
  {
    base: API_URLS.BASE_COUNTRY.COUNTRY,
    list: API_URLS.BASE_COUNTRY.GET_COUNTRIES_LIST,
    options: API_URLS.BASE_COUNTRY.GET_COUNTRY_OPTIONS,
    byId: API_URLS.BASE_COUNTRY.COUNTRY_BY_ID,
    move: API_URLS.BASE_COUNTRY.MOVE,
  },
  "country",
);

export const baseCurrencyService = makeRefService<BaseCurrency>(
  {
    base: API_URLS.BASE_CURRENCY.CURRENCY,
    list: API_URLS.BASE_CURRENCY.GET_CURRENCIES_LIST,
    options: API_URLS.BASE_CURRENCY.GET_CURRENCY_OPTIONS,
    byId: API_URLS.BASE_CURRENCY.CURRENCY_BY_ID,
    move: API_URLS.BASE_CURRENCY.MOVE,
  },
  "currency",
);
